/**
 * External Imports
 */
import { FC, useMemo, useRef, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

/**
 * Nexus Imports
 */
import { NeuIcon, NeuButton } from '@neutron/react';
import { useAnalyticsApi } from '@shared-web-analytics/react/dist';

/**
 * Internal Imports
 */
import { Dropdown } from '../../shared/dropdowns';

import { RootState } from '../../../redux/store';
import {
  clearPatientRoundHistory,
  clearTasksByAccountNum
} from '../../../redux/actions';

import { handleModal } from '../../../services/ModalPortal/utils';

import { logRoundProfile } from '../../../utils/analyticsHelpers';

/**
 * Global Type Definition Imports
 */
import { Patient, Unit } from '../../../config/interfaces';

/**
 * Style Imports
 */
import {
  CardHeader,
  ModalCardContent,
  InputHeading,
  ModalFooter,
  ModalInput,
  UnlistedPatientRoundCard,
  ModalTitle
} from './UnlistedPatientRoundModal.styles';

const { v1: uuidv1 } = require('uuid');

interface ModalProps {
  units: Unit[];
}

const UnlistedPatientRoundModal: FC<ModalProps> = ({ units }) => {
  const dispatch = useDispatch();
  const [paramsToPost, setParamsToPost] = useState<Patient>({} as Patient);
  const [open, setOpen] = useState(false);
  const modalContentRef = useRef<HTMLNeuCardElement>(null);

  const { logTrackingEvent } = useAnalyticsApi();
  const navigate = useNavigate();

  const showButton = useMemo(
    () =>
      paramsToPost.room &&
      paramsToPost.bed &&
      paramsToPost.lastName &&
      paramsToPost.firstName &&
      paramsToPost.unitId,
    [paramsToPost]
  );

  const handleDropdownChange = (name: string, value: string) => {
    setParamsToPost({ ...paramsToPost, [name]: value });
  };

  const handleInputs = (event: CustomEvent<any>, n?: string) => {
    const target = event.target as HTMLInputElement;
    let name = n;
    if (!n) {
      name = target.name;
    }
    const { value } = event.target as HTMLInputElement;
    if (paramsToPost) {
      switch (name) {
        case 'firstName':
        case 'lastName':
        case 'bed':
        case 'room':
          setParamsToPost({ ...paramsToPost, [name]: value });
          break;
        default: {
          console.log('error');
        }
      }
    }
  };

  const handlePost = () => {
    if (paramsToPost && showButton) {
      const unlistedUniqId = uuidv1();
      // TODO: Revisit unneeded params
      paramsToPost.accountNum = `UL-${unlistedUniqId}`;
      paramsToPost.mrn = `UL-${unlistedUniqId}`;
      // paramsToPost.chiefComplaint = 'NA';
      // paramsToPost.attendingPhysician = 'NA';
      // paramsToPost.lengthOfStay = 'NA';
      paramsToPost.sex = '';
      // paramsToPost.chiefComplaint = 'NA';

      dispatch(clearPatientRoundHistory());
      dispatch(clearTasksByAccountNum());
      logTrackingEvent(logRoundProfile());
      navigate('/patients/profile', {
        state: { patientMeta: { ...paramsToPost }, isUnlistedPatient: true }
      });
    }
    handleModal(modalContentRef);
  };

  return (
    <UnlistedPatientRoundCard
      id="Unlisted-Patient-Modal"
      className="show-modal"
      unit="card"
      ref={modalContentRef}
    >
      <CardHeader className="neu-bg-plain-0" unit="header">
        <ModalTitle>
          <NeuIcon size="3x" color="blue" style={{ alignItems: 'center' }}>
            supervisor_account
          </NeuIcon>
          Unlisted Patient Round
        </ModalTitle>
        <NeuButton
          aria-label="close panel"
          data-testid="unlisted-patient-close-button"
          kind="tertiary"
          color="blue"
          icon="close"
          size="small"
          onClick={() => handleModal(modalContentRef)}
        >
          Close
        </NeuButton>
      </CardHeader>
      <ModalCardContent unit="content">
        <InputHeading>Unit</InputHeading>
        <Dropdown
          classes="ps-0"
          name="unitId"
          open={open}
          placeholder={
            units.find((unit: Unit) => unit.unitId === paramsToPost.unitId)
              ?.unit || 'Select Unit'
          }
          setOpen={setOpen}
        >
          {units.map((unit: Unit) => (
            <div
              key={unit.unitId}
              aria-selected={paramsToPost?.unitId === unit.unitId}
              className={`dropdown-hover-item${
                paramsToPost?.unitId === unit.unitId ? ' selected' : ''
              }`}
              role="option"
              tabIndex={0}
              onClick={() => handleDropdownChange('unitId', unit.unitId)}
              onKeyDown={() => handleDropdownChange('unitId', unit.unitId)}
            >
              {unit.unit}
            </div>
          ))}
        </Dropdown>

        <ModalInput
          id="Unlisted-Patient-First-Name-Input"
          className="w-100"
          label="First Name"
          name="firstName"
          autocomplete="off"
          inputMode="text"
          autoCorrect="off"
          required
          onNeuChange={handleInputs}
        />
        <ModalInput
          id="Unlisted-Patient-Last-Name-Input"
          className="w-100"
          label="Last Name"
          name="lastName"
          inputMode="text"
          autoCorrect="off"
          required
          onNeuChange={handleInputs}
        />
        <div className="d-flex flex-1 justify-content-between position-relative">
          <div className="d-inline position-relative me-1 w-50">
            <ModalInput
              id="Unlisted-Patient-Room-Num-Input"
              autocomplete="off"
              autoCorrect="off"
              inputMode="text"
              label="Room No."
              name="room"
              required
              onNeuChange={handleInputs}
            />
          </div>
          <div className="d-inline position-relative ms-1 w-50">
            <ModalInput
              id="Unlisted-Patient-Bed-Num-Input"
              autocomplete="off"
              autoCorrect="off"
              inputMode="text"
              label="Bed No."
              name="bed"
              required
              onNeuChange={handleInputs}
            />
          </div>
        </div>
      </ModalCardContent>
      <ModalFooter unit="footer">
        <NeuButton
          color="blue"
          data-testid="unlisted-patient-start-round-button"
          disabled={!showButton ? true : undefined}
          size="small"
          style={{ marginRight: 20, marginBottom: 26, marginLeft: 263 }}
          onClick={showButton ? handlePost : undefined}
        >
          Begin Round
        </NeuButton>
      </ModalFooter>
    </UnlistedPatientRoundCard>
  );
};

const mapReduxStateToProps = (state: RootState) => {
  return {
    modalState: state.ModalReducer.modal,
    units: state.ConfigReducer.units
  };
};

export default connect(mapReduxStateToProps)(UnlistedPatientRoundModal);
